export interface RevealableMove {
  id: string | number;
  child_moves?: RevealableMove[];
  [key: string]: any;
}

/**
 * For hide_moves-mode chapters: given the full mainline (with branches
 * nested under child_moves) and the id of the move the student has
 * currently reached, returns a tree containing only what's been reached so
 * far — the mainline up to that move, plus the branch path taken to get
 * there if the student is inside a variation. Every node's own child_moves
 * are stripped, since those represent the (still-hidden) move that comes
 * next. Returns [] if nothing has been played yet.
 */
export function truncateMovesForReveal(
  moves: RevealableMove[] | undefined | null,
  revealUpToId: string | number | null | undefined,
): RevealableMove[] {
  if (!moves || moves.length === 0) return [];
  if (revealUpToId == null || revealUpToId === 'start') return [];

  const findPath = (
    nodes: RevealableMove[],
    path: RevealableMove[],
  ): RevealableMove[] | null => {
    for (const node of nodes) {
      const nextPath = [...path, node];
      if (node.id === revealUpToId) return nextPath;
      if (node.child_moves?.length) {
        const found = findPath(node.child_moves, nextPath);
        if (found) return found;
      }
    }
    return null;
  };

  const path = findPath(moves, []);
  if (!path) return [];

  const rootIndex = moves.findIndex(m => m.id === path[0].id);
  const visibleMainline = moves
    .slice(0, rootIndex + 1)
    .map(m => ({...m, child_moves: []}));

  if (path.length > 1) {
    let branchTail: RevealableMove[] = [];
    for (let i = path.length - 1; i >= 1; i--) {
      branchTail = [{...path[i], child_moves: branchTail}];
    }
    visibleMainline[visibleMainline.length - 1] = {
      ...visibleMainline[visibleMainline.length - 1],
      child_moves: branchTail,
    };
  }

  return visibleMainline;
}
